/** Field-side auto-open listener for the operator's whiteboard.
 *
 * The operator clicks "Open whiteboard" in their toolbar and the
 * WhiteboardPanel mounts on their side. Without this component, the
 * field user would never see the drawing — there's no field-side
 * button, and the WhiteboardPanel's inbound bus subscription only
 * attaches once it's mounted, so scene patches would be silently
 * dropped.
 *
 * Behaviour:
 *   - Listens for whiteboard/opened and whiteboard/closed from the
 *     operator. Mounts/unmounts a read-only WhiteboardPanel.
 *   - Falls back to opening on the first inbound scene.patch in case
 *     the field user joined AFTER the operator opened the whiteboard
 *     (the opened event arrived before this listener was up).
 *   - "Back to conversation" lets the field user dismiss the panel
 *     locally even while the operator's still drawing — they'll see
 *     it again on the next patch.
 */
import { useEffect, useState } from "react";

import { useSessionBus } from "@/lib/sessionBus";

import { WhiteboardPanel } from "./WhiteboardPanel";


type Props = {
  sessionId: string;
};


export function WhiteboardAutoOpen({ sessionId }: Props) {
  const bus = useSessionBus((s) => s.bus);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!bus) return;
    const offOpened = bus.on("whiteboard/opened", () => setOpen(true));
    const offClosed = bus.on("whiteboard/closed", () => setOpen(false));
    // Late-joiner fallback: any inbound patch means the operator's
    // whiteboard is live. Open the panel so the patch handler inside
    // it can apply the scene.
    const offPatch = bus.on("whiteboard/scene.patch", () => {
      setOpen((prev) => prev || true);
    });
    return () => {
      offOpened();
      offClosed();
      offPatch();
    };
  }, [bus]);

  if (!open) return null;

  return (
    <WhiteboardPanel
      sessionId={sessionId}
      bus={bus}
      canSave={false}
      onClose={() => setOpen(false)}
    />
  );
}
